<template>
  <VaDropdown v-model="isOpen" :offset="[13, 0]" placement="bottom-end" class="language-switcher" stick-to-edges>
    <template #anchor>
      <VaButton
        preset="secondary"
        color="textPrimary"
        round
        class="language-switcher__button"
        :aria-label="t('language.label')"
      >
        <span class="language-switcher__flag fi" :class="`fi-${currentLanguage.flagCode}`" aria-hidden="true" />
      </VaButton>
    </template>

    <VaDropdownContent class="language-switcher__content px-0 py-2" :style="{ '--hover-color': hoverColor }">
      <header class="language-switcher__header">
        {{ t('language.label') }}
      </header>

      <VaList>
        <VaListItem
          v-for="language in APP_LANGUAGES"
          :key="language.code"
          class="language-switcher__item"
          :class="{ 'language-switcher__item--active': language.code === currentLocale }"
          role="button"
          tabindex="0"
          @click="changeLanguage(language.code)"
          @keydown.enter="changeLanguage(language.code)"
        >
          <span class="language-switcher__item-flag fi" :class="`fi-${language.flagCode}`" aria-hidden="true" />
          <span class="language-switcher__item-label">{{ language.nativeName }}</span>
        </VaListItem>
      </VaList>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { APP_LANGUAGES, type AppLanguageCode } from '../../../i18n/app-languages'

const { locale, t } = useI18n()
const { colors, setHSLAColor } = useColors()

const isOpen = ref(false)
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const currentLocale = computed(() => locale.value)
const currentLanguage = computed(
  () => APP_LANGUAGES.find((language) => language.code === locale.value) ?? APP_LANGUAGES[0],
)

const changeLanguage = (code: AppLanguageCode) => {
  locale.value = code
  localStorage.setItem('preferred-language', code)
  isOpen.value = false
}
</script>

<style lang="scss">
.language-switcher {
  cursor: pointer;

  .va-dropdown__anchor {
    display: inline-block;
  }

  &__button {
    width: 36px;
    min-width: 36px;
    height: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__button .va-button__content {
    width: 100%;
    height: 100%;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
  }

  &__flag.fi {
    flex-shrink: 0;
    min-width: 1.375rem;
    width: 1.375rem;
    line-height: 1.03125rem;
    height: auto;
    border-radius: 2px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-size: cover;
  }

  &__content {
    width: max-content;
    min-width: 9.75rem;
    max-width: min(11.25rem, calc(100vw - 2rem));
  }

  &__header {
    padding: 0.25rem 0.75rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--va-secondary);
    opacity: 0.85;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 2.25rem;
    padding: 0 0.75rem;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover,
    &:focus-visible {
      background: var(--hover-color);
      outline: none;
    }

    &--active {
      background: color-mix(in srgb, var(--va-primary) 10%, transparent);
    }
  }

  &__item-flag.fi {
    flex-shrink: 0;
    width: 1.25rem;
    line-height: 0.9375rem;
    height: auto;
    margin-right: 0.75rem;
    border-radius: 2px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-size: cover;
  }

  &__item-label {
    flex: 1;
    min-width: 0;
    font-size: 0.875rem;
    line-height: 1.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
